import { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useRegister } from '@/features/auth/hooks/useRegister';

const emailRegex = /^[\w.-]+@[\w.-]+\.\w+$/;
const phoneRegex = /^\+7\(\d{3}\)-\d{3}-\d{2}-\d{2}$/;
const policyRegex = /^\d{18}$/;

export const RegisterPage = () => {
    const navigate = useNavigate();
    const { register } = useRegister();

    const [form, setForm] = useState({
        lastName: '',
        firstName: '',
        middleName: '',
        birthDate: '',
        phone: '',
        email: '',
        policyNumber: '',
        password: '',
        confirmPassword: '',
    });

    const [show1, setShow1] = useState(false);
    const [show2, setShow2] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);

    const handleChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        if (key === 'phone') {
            const digits = value.replace(/\D/g, '');
            value = '+7(' + digits.slice(1, 4) + ')-' + digits.slice(4, 7) + '-' + digits.slice(7, 9) + '-' + digits.slice(9, 11);
            value = value.replace(/(-)+$/, '');
        }

        if (key === 'policyNumber') {
            value = value.replace(/\D/g, '').slice(0, 18);
        }

        setForm(prev => ({ ...prev, [key]: value }));
    };

    const isFieldInvalid = (key: keyof typeof form) => {
        if (!touched) return false;
        const val = form[key];
        if (!val) return true;

        switch (key) {
            case 'email':
                return !emailRegex.test(val);
            case 'phone':
                return !phoneRegex.test(val);
            case 'policyNumber':
                return !policyRegex.test(val);
            case 'password':
                return val.length < 8;
            default:
                return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        setError(null);

        const hasEmpty = Object.values(form).some((v) => v === '');
        if (hasEmpty) {
            setError('Пожалуйста, заполните все поля');
            return;
        }

        if (form.password.length < 8) {
            setError('Пароль слишком короткий. Минимум 8 символов.');
            return;
        }

        if (form.password !== form.confirmPassword) {
            setError('Введённые вами пароли не совпадают!');
            return;
        }

        if (!emailRegex.test(form.email) || !phoneRegex.test(form.phone) || !policyRegex.test(form.policyNumber)) {
            setError('Некорректные данные в одном или нескольких полях');
            return;
        }

        const result = await register(form);
        if (result.success) {
            navigate('/registration-success');
        } else {
            setError(result.error);
        }

    };

    return (
        <main className="layout__content">
            <section className="section layout__section auth">
                <div className="container">
                    <div className="auth__block auth-block">
                        <header className="auth-block__header" style={{ textAlign: 'center' }}>
                            <h1 className="title auth-block__title">Регистрация</h1>
                        </header>

                        <form className="form auth__form auth-form" onSubmit={handleSubmit} noValidate>
                            {error && <div style={{ color: '#EA3323', marginBottom: 16 }}>{error}</div>}

                            <fieldset className="auth-form__fields">
                                {[
                                    { id: 'lastName', label: 'Фамилия', placeholder: 'Иванов' },
                                    { id: 'firstName', label: 'Имя', placeholder: 'Иван' },
                                    { id: 'middleName', label: 'Отчество', placeholder: 'Иванович' },
                                    { id: 'birthDate', label: 'Дата рождения', placeholder: '11.21.1998', type: 'date' },
                                    { id: 'phone', label: 'Номер телефона', placeholder: '+7(999)-123-45-67' },
                                    { id: 'email', label: 'Email', placeholder: 'example@gmail.com', type: 'email' }
                                ].map(({ id, label, placeholder, type }) => (
                                    <div className="form__row" key={id}>
                                        <div className="input">
                                            <label htmlFor={id} className="input__label input-label">{label}</label>
                                            <label className={clsx("text-input", {
                                                'text-input--error': isFieldInvalid(id as keyof typeof form)
                                            })}>
                                                <input
                                                    id={id}
                                                    className="text-input__input"
                                                    placeholder={placeholder}
                                                    type={type || 'text'}
                                                    value={form[id as keyof typeof form]}
                                                    onChange={handleChange(id as keyof typeof form)}
                                                    required
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ))}

                                <div className="form__row">
                                    <div className="input">
                                        <label htmlFor="policyNumber" className="input__label input-label">Номер полиса</label>
                                        <label className={clsx("text-input", {
                                            'text-input--error': isFieldInvalid('policyNumber')
                                        })}>
                                            <input
                                                id="policyNumber"
                                                className="text-input__input"
                                                placeholder="433200502304243423"
                                                value={form.policyNumber}
                                                onChange={handleChange('policyNumber')}
                                                required
                                            />
                                        </label>
                                        <p
                                            style={{
                                                color: isFieldInvalid('policyNumber') ? '#EA3323' : '#000',
                                                textAlign: 'center',
                                                fontSize: 14,
                                                marginTop: 6,
                                            }}
                                        >
                                            Номер полиса — обязательный элемент для регистрации корпоративных клиентов.
                                            Пожалуйста, введите номер полиса, чтобы завершить регистрацию.
                                        </p>
                                    </div>
                                </div>

                                {['password', 'confirmPassword'].map((field, i) => {
                                    const isPasswordShort = form.password.length < 8 && touched;
                                    const isConfirmError = field === 'confirmPassword' && form.password !== form.confirmPassword && touched;

                                    return (
                                        <div className="form__row" key={field}>
                                            <div className="input">
                                                <label htmlFor={field} className="input__label input-label">
                                                    {field === 'password' ? 'Пароль' : 'Подтвердите пароль'}
                                                </label>
                                                <label className={clsx("text-input", {
                                                    'text-input--error': (field === 'password' && isPasswordShort) || isConfirmError
                                                })}>
                                                    <input
                                                        id={field}
                                                        className="text-input__input"
                                                        placeholder={field === 'password' ? 'Придумайте пароль' : 'Повторите пароль'}
                                                        type={(i === 0 ? show1 : show2) ? 'text' : 'password'}
                                                        value={form[field as keyof typeof form]}
                                                        onChange={handleChange(field as keyof typeof form)}
                                                        required
                                                    />
                                                    <div className="text-input__control">
                                                        <button
                                                            type="button"
                                                            onClick={() => i === 0 ? setShow1(p => !p) : setShow2(p => !p)}
                                                            className="text-input__icon text-input__password-toggle"
                                                        >
                                                            <svg className="svg-icon">
                                                                <use href={`${
                                                                    (i === 0 ? show1 : show2)
                                                                        ? '/img/sprite-icons.svg#eye-slash-fill'
                                                                        : '/img/sprite-icons.svg#eye-fill'
                                                                }`} />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </label>
                                                {field === 'confirmPassword' && isPasswordShort && (
                                                    <p
                                                        style={{
                                                            color: '#EA3323',
                                                            marginTop: 6,
                                                            fontSize: 14,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        Пароль слишком короткий. Минимум 8 символов.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </fieldset>

                            <fieldset className="auth-form__submit">
                                <p className="auth-form__submit-text">
                                    Нажимая кнопку "Зарегистрироваться", вы принимаете{' '}
                                    <a className="form__link" href="/privacy-policy">политику конфиденциальности</a> и{' '}
                                    <a className="form__link" href="/personal-data">соглашение об обработке данных</a>
                                </p>
                                <button
                                    type="submit"
                                    className="button button_outline button_block auth-form__submit-button"
                                >
                                    Зарегистрироваться
                                </button>
                            </fieldset>

                            <fieldset className="auth-form__hint">
                                <span className="auth-form__hint-text">Уже зарегистрировались?</span>{' '}
                                <a className="form__link" href="/login">Войти</a>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </main>
    );
};
